// The floating day pill (PARITY P309; Mac MacDayPillOverlay, iOS dayPillVisible, Android
// FloatingDayPill): while the history moves, the TOP row's day floats over it and fades 1.4 s after
// the list stops (core/dayPill.ts). Near the newest messages it never shows.
//
// This component is the only thing that re-renders as rows scroll past: it reads the scroller
// itself (passive scroll listener, one probe per frame) and never touches the conversation's state.
import { useEffect, useRef, useState, type RefObject } from 'react';
import { DayPillModel, type DayPillState } from '../../../core/dayPill';
import { MessageListDerivation } from '../../../core/messageListDerivation';
import './extras.css';

/** Within this many px of the newest message the pill stays hidden (Conversation's `atBottom`). */
const NEAR_BOTTOM_PX = 80;

/** The first message row under the scroller's top edge (glass bars may overlap it: look past them). */
function topRow(scroller: HTMLElement): { id: string; at: number } | null {
  const r = scroller.getBoundingClientRect();
  const x = r.left + r.width / 2;
  for (const dy of [12, 48, 96, 160]) {
    const y = r.top + dy;
    if (y > r.bottom) break;
    for (const el of document.elementsFromPoint(x, y)) {
      if (!scroller.contains(el)) continue;
      const row = el.closest<HTMLElement>('[data-post-id]');
      const time = row?.querySelector('time[datetime]');
      const at = time ? Date.parse(time.getAttribute('datetime') ?? '') : NaN;
      if (row && Number.isFinite(at)) return { id: row.dataset.postId!, at };
    }
  }
  return null;
}

export function DayPill({ scroller, reversed = true }: { scroller: RefObject<HTMLElement | null>; reversed?: boolean }) {
  const [state, setState] = useState<DayPillState>({ topPostId: null, visible: false });
  const [label, setLabel] = useState<string | null>(null);
  const [top, setTop] = useState(8);
  const model = useRef<DayPillModel | null>(null);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const m = new DayPillModel(setState);
    model.current = m;
    let frame = 0;
    const probe = () => {
      frame = 0;
      const nearBottom = reversed ? el.scrollTop > -NEAR_BOTTOM_PX : el.scrollHeight - el.clientHeight - el.scrollTop < NEAR_BOTTOM_PX;
      const row = topRow(el);
      if (!row) return;
      setLabel(MessageListDerivation.dayLabel(row.at, { now: Date.now() }));
      // Under the header (a glass header floats OVER the history: the scroller starts beneath it).
      const pane = el.offsetParent as HTMLElement | null;
      const header = pane?.querySelector<HTMLElement>(':scope > .conversation-header');
      const below = header && pane ? header.getBoundingClientRect().bottom - pane.getBoundingClientRect().top : el.offsetTop;
      setTop(Math.max(el.offsetTop, below) + 8);
      m.topRowChanged(row.id, nearBottom);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(probe);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
      m.dispose();
    };
  }, [scroller, reversed]);

  if (!label || !state.topPostId) return null;
  return (
    <div className={`day-pill${state.visible ? ' visible' : ''}`} style={{ top }} aria-hidden={!state.visible} data-day-pill={state.visible ? label : ''}>
      {label}
    </div>
  );
}
